import { React, useState } from 'react';

import { FormHelperText } from '@mui/material';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded';
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import MemoryRoundedIcon from '@mui/icons-material/MemoryRounded';
import MonitorHeartRoundedIcon from '@mui/icons-material/MonitorHeartRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import SettingsInputComponentRoundedIcon from '@mui/icons-material/SettingsInputComponentRounded';

import { Alerta } from '../../components/alerta';
import DayDatePicker from '../../components/dayDatePicker';
import PageHeader from '../../components/pageHeader';
import SubmitButton from '../../components/submitButton';
import { formatDateLabelPtBr, formatIsoDate } from '../../utils/dateUtils';
import { fetchFlask } from '../../utils/fetchFlask';
import '../css/inverterReplacement.css';

const SubstituirInversor = () => {
    const [dateTime, setDateTime] = useState(null);
    const [error, setError] = useState(false);
    const [monitoramento, setMonitoramento] = useState('');
    const [modelo, setModelo] = useState('');
    const [potNom, setPotNom] = useState('');
    const [potPico, setPotPico] = useState('');
    const [inversorAntigo, setInversorAntigo] = useState('');
    const [numSerie, setNumSerie] = useState('');
    const [message, setMessage] = useState('');
    const [status, setStatus] = useState('');
    const [openAlerta, setOpenAlerta] = useState(false);
    const [isSubmit, setIsSubmit] = useState(false);

    async function submitInversorChange (e) {
        e.preventDefault();
        setIsSubmit(true);

        let data = '';
        if (dateTime) {
            setError(false);
            data = formatIsoDate(dateTime);
        } else {
            setError(true);
            setIsSubmit(false);
            return;
        }
        setError(false);

        const body = JSON.stringify({
            dataTroca: data,
            inversorAntigo,
            monitoramento,
            inversor: modelo,
            potencia_nominal: potNom,
            potencia_pico: potPico,
            num_serie: numSerie
        });

        try {
            const response = await fetchFlask('substituirInversor', body);
            setMessage(response.message);
            setStatus(response.status);
            setOpenAlerta(true);
            if (response.status === 'success') {
                setDateTime(null);
                setInversorAntigo('');
                setNumSerie('');
                setPotNom('');
                setPotPico('');
                setModelo('');
                setMonitoramento('');
            }
        } finally {
            setIsSubmit(false);
        }
    }

    const dataLabel = formatDateLabelPtBr(dateTime);
    const completedFields = [
        dateTime,
        inversorAntigo,
        numSerie,
        potNom,
        modelo,
        monitoramento
    ].filter(Boolean).length;
    const potenciaLabel = [
        potNom && `${potNom} nominal`,
        potPico && `${potPico} pico`
    ].filter(Boolean).join(' / ') || 'Não informada';

    return (
        <Box className='mainBox inverter-replacement-page'>
            <Alerta
                texto={message}
                status={status}
                open={openAlerta}
                handleClose={() => setOpenAlerta(false)}
            />
            <PageHeader>Substituir Inversor</PageHeader>

            <form action='' method='POST' id='formReplaceInversor' onSubmit={submitInversorChange} className='inverter-replacement-layout'>
                <Box className='inverter-replacement-workspace'>
                    <Box className='inverter-replacement-intro'>
                        <Box>
                            <h2>Troca de inversor</h2>
                        </Box>
                        <p>
                            Informe a data da troca, o serial antigo e os dados do novo inversor para atualizar o cadastro.
                        </p>
                    </Box>

                    <Box className='inverter-replacement-panel'>
                        <Box className='inverter-replacement-section-heading'>
                            <CalendarMonthRoundedIcon />
                            <Box>
                                <span>Etapa 1</span>
                                <strong>Data e inversor antigo</strong>
                            </Box>
                        </Box>

                        <Box className='inverter-replacement-fields-grid'>
                            <FormControl className='inverter-replacement-date-control' error={error}>
                                <DayDatePicker
                                    id='data'
                                    label='Dia da troca'
                                    minDate={new Date('2023-01-02')}
                                    maxDate={new Date()}
                                    value={dateTime}
                                    onChange={setDateTime}
                                />
                                {error && (
                                    <FormHelperText>Por favor selecione uma data.</FormHelperText>
                                )}
                            </FormControl>

                            <TextField
                                label='Inversor antigo - serial'
                                id='OldInverter'
                                value={inversorAntigo}
                                onChange={(e) => setInversorAntigo(e.target.value)}
                                required
                            />
                        </Box>
                    </Box>

                    <Box className='inverter-replacement-panel'>
                        <Box className='inverter-replacement-section-heading'>
                            <MemoryRoundedIcon />
                            <Box>
                                <span>Etapa 2</span>
                                <strong>Novo inversor</strong>
                            </Box>
                        </Box>

                        <Box className='inverter-replacement-fields-grid'>
                            <TextField
                                label='Número de série'
                                id='numSerie'
                                value={numSerie}
                                onChange={(e) => setNumSerie(e.target.value)}
                                required
                            />
                            <TextField
                                label='Modelo do inversor'
                                id='modelo'
                                value={modelo}
                                onChange={(e) => setModelo(e.target.value)}
                                required
                            />
                            <TextField
                                label='Potência nominal'
                                id='kWn'
                                value={potNom}
                                onChange={(e) => setPotNom(e.target.value)}
                                required
                            />
                            <TextField
                                label='Potência pico'
                                id='kWp'
                                value={potPico}
                                onChange={(e) => setPotPico(e.target.value)}
                            />
                        </Box>
                    </Box>

                    <Box className='inverter-replacement-panel'>
                        <Box className='inverter-replacement-section-heading'>
                            <MonitorHeartRoundedIcon />
                            <Box>
                                <span>Etapa 3</span>
                                <strong>Portal de monitoramento</strong>
                            </Box>
                        </Box>

                        <Box className='inverter-replacement-monitoring-strip'>
                            <SettingsInputComponentRoundedIcon />
                            <FormControl required className='inverter-replacement-select-control'>
                                <InputLabel id='labelMon'>Portal de Monitoramento</InputLabel>
                                <Select
                                    labelId='labelMon'
                                    label='Portal de Monitoramento'
                                    value={monitoramento}
                                    onChange={(e) => setMonitoramento(e.target.value)}
                                >
                                    <MenuItem value='CANADIAN'>CANADIAN</MenuItem>
                                    <MenuItem value='SUNGROW'>SUNGROW</MenuItem>
                                    <MenuItem value='SICES'>SICES</MenuItem>
                                    <MenuItem value='SOLARVIEW'>SOLARVIEW</MenuItem>
                                    <MenuItem value='GROWATT'>GROWATT</MenuItem>
                                    <MenuItem value='FRONIUS'>FRONIUS</MenuItem>
                                    <MenuItem value='SUNWAYS'>SUNWAYS</MenuItem>
                                    <MenuItem value='PV OPERATION'>PV OPERATION</MenuItem>
                                    <MenuItem value='CANADIAN NEW'>CANADIAN NEW</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                    </Box>
                </Box>

                <Box className='inverter-replacement-summary'>
                    <Box className='inverter-replacement-summary-header'>
                        <FactCheckRoundedIcon />
                        <Box>
                            <span>Resumo</span>
                            <strong>Revisão da substituição</strong>
                        </Box>
                    </Box>

                    <Box className='inverter-replacement-summary-list'>
                        <Box>
                            <span>Data da troca</span>
                            <strong className={!dateTime ? 'is-empty' : undefined}>{dataLabel}</strong>
                        </Box>
                        <Box>
                            <span>Serial antigo</span>
                            <strong className={!inversorAntigo ? 'is-empty' : undefined}>{inversorAntigo || 'Não informado'}</strong>
                        </Box>
                        <Box>
                            <span>Novo serial</span>
                            <strong className={!numSerie ? 'is-empty' : undefined}>{numSerie || 'Não informado'}</strong>
                        </Box>
                        <Box>
                            <span>Modelo</span>
                            <strong className={!modelo ? 'is-empty' : undefined}>{modelo || 'Não informado'}</strong>
                        </Box>
                        <Box>
                            <span>Potências</span>
                            <strong className={!potNom && !potPico ? 'is-empty' : undefined}>{potenciaLabel}</strong>
                        </Box>
                        <Box>
                            <span>Portal</span>
                            <strong className={!monitoramento ? 'is-empty' : undefined}>{monitoramento || 'Não selecionado'}</strong>
                        </Box>
                    </Box>

                    <Box className='inverter-replacement-attention'>
                        <AutorenewRoundedIcon />
                        <p>
                            Revise os seriais antes de confirmar. A operação substitui o inversor antigo pelo novo cadastro informado.
                        </p>
                    </Box>

                    <Box className='inverter-replacement-completion'>
                        <AssignmentTurnedInRoundedIcon />
                        <Box>
                            <span>Campos críticos</span>
                            <strong>{completedFields}/6 preenchidos</strong>
                        </Box>
                    </Box>

                    <SubmitButton isSubmit={isSubmit} startIcon={<SendRoundedIcon />}>
                        Confirmar Substituição
                    </SubmitButton>
                </Box>
            </form>
        </Box>
    );
};

export default SubstituirInversor;
